import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  type CartItem,
  type ShopifyProduct,
  CART_QUERY,
  storefrontApiRequest,
  createShopifyCart,
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
} from '@/lib/shopify';

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isOpen: boolean;
  isLoading: boolean;
  isSyncing: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, 'lineId'>) => Promise<void>;
  buyNow: (item: Omit<CartItem, 'lineId'>) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
  totalItems: () => number;
  subtotal: () => number;
}

const getItemAmount = (item: CartItem) => Number.parseFloat(item.price.amount) || 0;

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isOpen: false,
      isLoading: false,
      isSyncing: false,

      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existingItem = items.find((i) => i.variantId === item.variantId);

        set({ isLoading: true });
        try {
          if (!cartId) {
            // No cart yet — create one
            const result = await createShopifyCart({ ...item, lineId: null });
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [{ ...item, lineId: result.lineId }],
                isOpen: true,
              });
            }
          } else if (existingItem) {
            // Item exists — update quantity
            const newQuantity = existingItem.quantity + item.quantity;
            if (!existingItem.lineId) {
              console.error('Cannot update item without lineId:', existingItem);
              return;
            }
            const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQuantity);
            if (result.success) {
              set({
                items: get().items.map((i) =>
                  i.variantId === item.variantId ? { ...i, quantity: newQuantity } : i
                ),
                isOpen: true,
              });
            } else if (result.cartNotFound) {
              clearCart();
            }
          } else {
            // New item — add line
            const result = await addLineToShopifyCart(cartId, { ...item, lineId: null });
            if (result.success) {
              set({
                items: [...get().items, { ...item, lineId: result.lineId ?? null }],
                isOpen: true,
              });
            } else if (result.cartNotFound) {
              clearCart();
            }
          }
        } catch (error) {
          console.error('Failed to add item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      buyNow: async (item) => {
        set({ isLoading: true });
        // Open window BEFORE async call to avoid popup blocker
        const checkoutWindow = window.open('about:blank', '_blank');
        try {
          const result = await createShopifyCart({ ...item, lineId: null });
          if (result) {
            set({
              cartId: result.cartId,
              checkoutUrl: result.checkoutUrl,
              items: [{ ...item, lineId: result.lineId }],
            });
            // Track checkout initiation
            if (typeof window !== "undefined") {
              if ((window as any).fbq) {
                (window as any).fbq("track", "InitiateCheckout", {
                  content_ids: [item.variantId],
                  num_items: item.quantity,
                  value: Number.parseFloat(item.price.amount) * item.quantity,
                  currency: "MXN",
                });
              }
              if ((window as any).gtag) {
                (window as any).gtag("event", "begin_checkout", {
                  currency: "MXN",
                  value: Number.parseFloat(item.price.amount) * item.quantity,
                  items: [{ item_id: item.variantId, price: Number.parseFloat(item.price.amount), quantity: item.quantity }],
                });
              }
            }
            if (checkoutWindow) {
              checkoutWindow.location.href = result.checkoutUrl;
            } else {
              // Fallback: if popup was still blocked, redirect current tab
              window.location.href = result.checkoutUrl;
            }
          } else {
            checkoutWindow?.close();
          }
        } catch (error) {
          console.error('Failed to buy now:', error);
          checkoutWindow?.close();
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }

        const { items, cartId, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (result.success) {
            set({ items: get().items.map((i) => (i.variantId === variantId ? { ...i, quantity } : i)) });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to update quantity:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const newItems = get().items.filter((i) => i.variantId !== variantId);
            newItems.length === 0 ? clearCart() : set({ items: newItems });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to remove item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });
        try {
          const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
          if (!data) return; // API error — preserve local cart
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) {
            // Fire purchase event before clearing — cart had items that are now completed
            const { items } = get();
            if (items.length > 0 && typeof window !== "undefined") {
              const total = items.reduce((s, i) => s + (Number.parseFloat(i.price.amount) || 0) * i.quantity, 0);
              const numItems = items.reduce((s, i) => s + i.quantity, 0);
              if ((window as any).fbq) {
                (window as any).fbq("track", "Purchase", {
                  content_ids: items.map((i) => i.variantId),
                  num_items: numItems,
                  value: total,
                  currency: "MXN",
                });
              }
              if ((window as any).gtag) {
                (window as any).gtag("event", "purchase", {
                  currency: "MXN",
                  value: total,
                  transaction_id: cartId,
                  items: items.map((i) => ({
                    item_id: i.variantId,
                    item_name: i.product.node.title,
                    price: Number.parseFloat(i.price.amount),
                    quantity: i.quantity,
                  })),
                });
              }
            }
            clearCart();
          }
        } catch (error) {
          console.error('Failed to sync cart:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      getCheckoutUrl: () => {
        const url = get().checkoutUrl;
        if (url && typeof window !== "undefined") {
          const { items } = get();
          const total = items.reduce((s, i) => s + (Number.parseFloat(i.price.amount) || 0) * i.quantity, 0);
          const numItems = items.reduce((s, i) => s + i.quantity, 0);
          if ((window as any).fbq) {
            (window as any).fbq("track", "InitiateCheckout", {
              content_ids: items.map((i) => i.variantId),
              num_items: numItems,
              value: total,
              currency: "MXN",
            });
          }
          if ((window as any).gtag) {
            (window as any).gtag("event", "begin_checkout", {
              currency: "MXN",
              value: total,
              items: items.map((i) => ({ item_id: i.variantId, price: Number.parseFloat(i.price.amount), quantity: i.quantity })),
            });
          }
        }
        return url;
      },
      totalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
      subtotal: () => get().items.reduce((sum, item) => sum + getItemAmount(item) * item.quantity, 0),
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        cartId: state.cartId,
        checkoutUrl: state.checkoutUrl,
      }),
    }
  )
);

// ── Helper to create a ShopifyProduct from local product data ──

export function createShopifyProductFromLocal(localProduct: {
  name: string;
  tagline: string;
  price: number;
  image: string;
  slug: string;
  shopifyVariantId: string;
}): {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  selectedOptions: Array<{ name: string; value: string }>;
} {
  return {
    product: {
      node: {
        id: '',
        title: localProduct.name,
        description: localProduct.tagline,
        handle: localProduct.slug,
        priceRange: {
          minVariantPrice: { amount: String(localProduct.price), currencyCode: 'MXN' },
        },
        images: {
          edges: [{ node: { url: localProduct.image, altText: localProduct.name } }],
        },
        variants: {
          edges: [
            {
              node: {
                id: localProduct.shopifyVariantId,
                title: 'Default Title',
                price: { amount: String(localProduct.price), currencyCode: 'MXN' },
                availableForSale: true,
                selectedOptions: [{ name: 'Title', value: 'Default Title' }],
              },
            },
          ],
        },
        options: [{ name: 'Title', values: ['Default Title'] }],
      },
    },
    variantId: localProduct.shopifyVariantId,
    variantTitle: 'Default Title',
    price: { amount: String(localProduct.price), currencyCode: 'MXN' },
    selectedOptions: [{ name: 'Title', value: 'Default Title' }],
  };
}
